import type { Au13RuleSet } from "./types.js";

// AU-13 CCS ruleset for FY2023-24 — modelled as TWO rule periods because the
// "Cheaper Child Care" reform took effect mid-year, from the first CCS
// fortnight on or after 10 July 2023. A backdated reclaim for FY2023-24 must
// resolve against whichever period the care fortnight fell in.
//
//  - Period A (1 Jul 2023 – 9 Jul 2023): the PRE-reform structure carried over
//    from FY2022-23 — max 85% at $69,390, taper 1% per $3,000, 0% at $353,680,
//    and NO higher-second-child rate.
//  - Period B (from 10 Jul 2023 – 30 Jun 2024): the post-reform structure —
//    max 90% at $80,000, taper 1% per $5,000, 0% at $530,000, plus the new
//    higher-second-child schedule.
//
// Both transcribed VERBATIM from the DSS Family Assistance Guide §1.1.A.70 as
// archived within FY2023-24 (pre-reform table and post-reform table), §3.5.3
// rate caps (the 7-Jul-2023 caps apply across the whole FY), and the Services
// Australia activity-test table (the activity test applied all year).

const fy2023_24_sources = {
  percentage: "https://guides.dss.gov.au/family-assistance-guide/1/1/a/70",
  incomeThresholds: "https://guides.dss.gov.au/family-assistance-guide/1/1/i/70",
  rateCaps: "https://guides.dss.gov.au/family-assistance-guide/3/5/3",
  hours:
    "https://www.servicesaustralia.gov.au/activity-level-and-subsidised-hours-for-child-care-subsidy",
} as const;

// The 2023-24 hourly rate caps (effective from 1 July 2023) and the activity
// test were unchanged by the 10 Jul income/percentage reform, so both periods
// share them.
const fy2023_24_rateCaps = {
  centreBasedBelowSchool: 13.73,
  centreBasedSchoolAged: 12.02,
  oshcBelowSchool: 13.73,
  oshcSchoolAged: 12.02,
  familyDayCare: 12.72,
  inHomeCarePerFamily: 37.34,
} as const;

const fy2023_24_hours = {
  mode: "activity_test" as const,
  activityTiers: [
    { fromHours: 8, toHours: 16, subsidisedHours: 36 },
    { fromHours: 16, toHours: 48, subsidisedHours: 72 },
    { fromHours: 48, toHours: null, subsidisedHours: 100 },
  ],
  lowIncomeSafetyNetHours: 24,
  guaranteeHours: 0,
  highTierHours: 100,
  highTierActivityThreshold: 48,
  indigenousHours: 36,
};

/** Period A — pre-reform (1 Jul 2023 – 9 Jul 2023). */
export const fy2023_24_preReform: Au13RuleSet = {
  product: "AU13",
  fy: "2023-24",
  effectiveFrom: "2023-07-01",
  effectiveTo: "2024-06-30",
  periodKey: "2023-24:pre-reform",
  periodLabel: "FY2023-24 pre-reform (1–9 Jul 2023)",
  periodFrom: "2023-07-01",
  periodTo: "2023-07-09",
  sources: fy2023_24_sources,
  sourcesVerifiedAt: "2026-06-13",
  // Pre-reform 2022-23 schedule (DSS §1.1.A.70 as archived Mar 2023), in force
  // 1–9 Jul 2023. It is STEPPED, not a single linear ramp: 85%→50% taper, a 50%
  // flat band, a 50%→20% taper, a 20% flat band, then 0%. Modelled via
  // `segments`; the linear `taperStep` documents the first band's rate but the
  // engine uses the segments. [AU-13 figure-audit F1 — see docs/au13-figure-audit.md]
  //   ≤ $69,390            85%
  //   $69,390–$174,390     85% → 50%  (−1% / $3,000)
  //   $174,390–$253,680    50%        (flat)
  //   $253,680–$343,680    50% → 20%  (−1% / $3,000)
  //   $343,680–$353,680    20%        (flat)
  //   > $353,680           0%
  standardTaper: {
    maxPercent: 0.85,
    maxThreshold: 69_390,
    taperStep: 3_000,
    zeroThreshold: 353_680,
    segments: [
      { fromThreshold: 69_390, fromPercent: 0.85, taperStep: 3_000 },
      { fromThreshold: 174_390, fromPercent: 0.5 },
      { fromThreshold: 253_680, fromPercent: 0.5, taperStep: 3_000 },
      { fromThreshold: 343_680, fromPercent: 0.2 },
    ],
  },
  // No higher-second-child rate existed before the 10 Jul 2023 reform.
  higherRate: null,
  hours: fy2023_24_hours,
  rateCaps: fy2023_24_rateCaps,
};

/** Period B — post Cheaper Child Care reform (10 Jul 2023 – 30 Jun 2024). */
export const fy2023_24_postReform: Au13RuleSet = {
  product: "AU13",
  fy: "2023-24",
  effectiveFrom: "2023-07-01",
  effectiveTo: "2024-06-30",
  periodKey: "2023-24:post-reform",
  periodLabel: "FY2023-24 post-reform (from 10 Jul 2023)",
  periodFrom: "2023-07-10",
  periodTo: "2024-06-30",
  sources: fy2023_24_sources,
  sourcesVerifiedAt: "2026-06-13",
  // Post-reform (§1.1.A.70 as archived Feb 2024): 90% up to $80,000, taper 1%
  // per $5,000, 0% at $530,000.
  standardTaper: {
    maxPercent: 0.9,
    maxThreshold: 80_000,
    taperStep: 5_000,
    zeroThreshold: 530_000,
  },
  // Higher CCS for second-and-younger children, introduced by the reform:
  // 95% up to $138,118, taper $3,000 to 80% flat ($183,118–$262,408), taper
  // $3,000 to 50% flat ($352,408–$362,408), then standard rate at $362,408.
  higherRate: {
    topThreshold: 138_118,
    topPercent: 0.95,
    taperStep: 3_000,
    midPercent: 0.8,
    midFromThreshold: 183_118,
    midToThreshold: 262_408,
    floorPercent: 0.5,
    revertThreshold: 362_408,
  },
  hours: fy2023_24_hours,
  rateCaps: fy2023_24_rateCaps,
};

/** The 10 July 2023 reform cut-over date — care on/after this falls in
 * Period B. Exposed so the reclaim helper can resolve the correct period. */
export const FY2023_24_REFORM_DATE = "2023-07-10";
